<template>
  <div class="ai-detection-flag">
    <div class="ai-detection-flag-box w-100">
      <eg-icon
        v-b-popover.hover.top="$t('essays.aiDetectionDisclaimer')"
        icon="information"
        class="mr-1"
      />
      <component :is="flagData.analysis" />
      <icon-badge :style="{ '--bg-color': badgeData.color }" class="ml-auto">
        <template #icon>
          <eg-icon :icon="badgeData.icon" size="small" />
        </template>
        {{ badgeData.text }}
      </icon-badge>
    </div>
    <next-steps-box
      :title="$t('essays.detectionDetails')"
      :icon="isHighConfidence ? 'warning' : 'bulb-light'"
      :color="isHighConfidence ? 'var(--colors-orange-500)' : '#17a2b8'"
      :text="flagData.suggestions"
      :is-list="true"
    />

    <div v-if="itemsWithColor.length" class="mt-4">
      <div class="ai-detection-flag-subtitle">
        {{ $t("essays.aiDetector.detectedInSentences") }}
      </div>
      <base-item
        v-for="(item, index) in itemsWithColor"
        ref="items"
        :key="item.id"
        class="flag-item usedAI"
        :style="{ '--type-background': config.background }"
        :active-highlight-index="activeHighlightIndex"
        :index="index"
        :item="item"
        next-steps-key="suggestions"
        :next-step-title="$t('essays.proTip')"
        :format-title="() => ''"
        v-on="$listeners"
      />
    </div>
  </div>
</template>

<script>
import NextStepsBox from "@/components/essay/essay/v3/next_steps_box.vue";
import IconBadge from "@/components/global/icon_badge.vue";
import BaseItem from "@/components/essay/essay/v3/base_item.vue";

export default {
  name: "AiDetectionFlag",

  components: {
    NextStepsBox,
    IconBadge,
    BaseItem,
  },

  props: {
    flagData: {
      type: Object,
      required: true,
    },

    items: {
      type: Array,
      required: true,
    },

    config: {
      type: Object,
      required: true,
    },

    isNoRanges: {
      type: Boolean,
      required: true,
    },

    activeHighlightIndex: {
      type: Number,
      required: true,
    },
  },

  computed: {
    itemsWithColor() {
      return this.items.map((item) => ({
        ...item,
        color: "#8A3FFC",
        backgroundColor: "#F6F2FF",
      }));
    },
    // high confidence considered to be if aiUsageScore > 33
    isHighConfidence() {
      return this.flagData?.flag?.summary?.aiUsageScore >= 33;
    },
    badgeText() {
      const score = this.flagData?.flag?.summary?.aiUsageScore;

      let text = "AI Detected";

      if (score < 33) {
        text = "No AI Detected";
      } else if (score <= 66) {
        text = "Some AI Detected";
      }

      return text;
    },
    badgeColor() {
      const score = this.flagData?.flag?.summary?.aiUsageScore;

      let color = "var(--color-surface-semantic-error-error)";

      if (score < 33) {
        color = "var(--color-surface-semantic-success-default)";
      } else if (score <= 66) {
        color = "var(--colors-orange-500)";
      }

      return color;
    },
    badgeData() {
      return {
        icon: this.isHighConfidence ? "AI-robot" : "check",
        text: this.badgeText,
        color: this.badgeColor,
      };
    },
  },

  watch: {
    items: {
      immediate: true,
      handler() {
        if (this.isNoRanges) return;

        this.$nextTick(() => {
          const acum = [];
          const itemsFromGroup = this.items;

          for (let index = 0; index < itemsFromGroup.length; index++) {
            const item = itemsFromGroup[index];
            const realIndex = index;

            if (!item.ranges) {
              return acum;
            }

            for (
              let rangeIndex = 0;
              rangeIndex < item.ranges.length;
              rangeIndex++
            ) {
              acum.push({
                range: item.ranges[rangeIndex],
                highlightIndex: realIndex + 1,
                showHighlightIndex: true,
                refComponent: this.$refs.items?.[realIndex],
                color: this.config.color,
                backgroundColor: this.config.background,
              });
            }
          }
          this.$emit("updateHighlights", acum);
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-detection-flag-box {
  display: flex;
  padding: 24px;
  align-items: center;
  gap: 8px;
  border-radius: var(--radius-rd-300, 8px);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
}

.icon-badge {
  --bg-color: var(--color-surface-semantic-success-default);
  border-radius: var(--radius-rd-500, 16px);
  background: var(--bg-color);
  color: var(--color-text-neutral-on-color, #fff);
}

.ai-detection-flag-subtitle {
  display: flex;
  padding: 0 0 12px 0;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  color: var(--color-text-neutral-on-white, #1c0f30);
  font-family: var(--typography-font-family-text-primary, Inter);
  font-size: var(--typography-font-size-fs-400, 16px);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-600, 600);
  line-height: 120%; /* 19.2px */
}

.flag-item {
  border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  background: var(--color-surface-neutral-background, #fff);
  margin: 0 -24px;
}
</style>
